"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import Container from "~/components/Container";
import CallToAction from "~/components/CallToAction";
import VideoPopup from "~/components/VideoPopup";
import { type VideosSection_Fields } from "~/__generated__/graphql";

export default function VideosSection({
  title,
  linkTitle,
  link,
  videos,
}: VideosSection_Fields) {
  const useableVideos =
    videos?.filter((item) => {
      if (item?.videopreview) return item;
    }) ?? [];

  // Current index is used to show current vide + title
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoPopup, setVideoPopup] = useState<string | undefined | null>();

  const videoElement = useRef<HTMLVideoElement>(null);
  const currentProgressBar = useRef<HTMLSpanElement | null>(null);
  const currentVideoMaxTime = useRef(0);

  const progressItemsContainer = useRef<HTMLDivElement>(null);
  const scrollContainer = useRef<HTMLDivElement>(null);

  const animationFrameId = useRef<number | null>(null);

  // When unmounting make sure we cancel animation frame.
  useEffect(() => {
    return () => {
      if (animationFrameId.current)
        cancelAnimationFrame(animationFrameId.current);
    };
  });

  function handleLoadMetaData() {
    // When we load the video, and when we change active video, we need to calc
    // the max time of a video to calculate the percentage.
    if (!videoElement?.current) return;
    const maxTime = videoElement.current?.duration || 1;
    currentVideoMaxTime.current = maxTime;
  }

  function handleVideoPlay() {
    // When the video starts playing, we want to update the currently relevent
    // progress bar.
    if (!progressItemsContainer?.current) return;
    const childrenItems = Array.from(progressItemsContainer.current.childNodes);
    const relevantChild = childrenItems[currentIndex];
    // If it doesnt' exist we cant update it correctly.
    if (!relevantChild) return;

    // There is only 1 span, which is the item we need to grow in width.
    currentProgressBar.current = (relevantChild as HTMLElement).querySelector(
      "span",
    );

    // When the video starts playing, we want to start an animationframe, so
    // that we can update the fake progress bar. Using animationFrame also
    // ensures that the progress and hte actual video progress aren't out of
    // sync (as it only updates every frame) We also want to make sure that the
    // relevant title + progress bar are in view, so we scroll to it.
    if (!videoElement?.current || !currentProgressBar) return;
    // const relevantChildOffsetTop = relevantChild.offsetTop;

    // TODO: Check if this is still needed. Did it work previously?
    // scrollTo(scrollContainer.current, relevantChildOffsetTop, 0, true);

    requestAnimationFrame(handleProgressBar);
  }

  function handleProgressBar() {
    if (!videoElement?.current || !currentVideoMaxTime?.current) return;
    const progressInSeconds = videoElement?.current?.currentTime;
    // Get % through video capped at 100%.
    const progressInPercent = Math.min(
      100,
      Math.round((progressInSeconds / currentVideoMaxTime.current) * 100),
    );

    // We use width, because with transform we cant correctly get the left part
    // of the bar roudned (untill its been fully translated.)
    if (currentProgressBar.current)
      currentProgressBar.current.style.width = `${progressInPercent}%`;
    if (progressInPercent === 100 && animationFrameId.current)
      return cancelAnimationFrame(animationFrameId.current);
    animationFrameId.current = requestAnimationFrame(handleProgressBar);
  }

  function handleVideoEnd() {
    // When the video ends, we need to start the next video in the array. If
    // this is the last video, we start again at the first video (even if it is
    // this video). When we loop, we also want to reset the progress meters.
    let newIndex = currentIndex + 1;
    if (currentIndex === useableVideos.length - 1) {
      newIndex = 0;
      resetAllProgressBars();
    }
    // If there is only 1 item, we need to manually replay. This cannot be done
    // with looping because it won't fire the onPlay event
    if (currentIndex === 0 && newIndex === 0) {
      void videoElement.current?.play();
    } else {
      updateCurrentVideo(newIndex);
    }
  }

  function updateCurrentVideo(index: number) {
    if (index === null || index === undefined || isNaN(index)) return;
    if (animationFrameId.current)
      cancelAnimationFrame(animationFrameId.current);
    const newVideo = useableVideos[index];

    if (!newVideo) return;
    setCurrentIndex(index);
  }

  function resetAllProgressBars() {
    // When we loop through all the videos we want to reset all progress bars.
    if (!progressItemsContainer?.current) return;
    const childrenItems = Array.from(progressItemsContainer.current.childNodes);
    for (const item of childrenItems) {
      // There is only 1 span (the progress bar)
      const progressBar = (item as HTMLElement)?.querySelector("span");
      if (progressBar) {
        progressBar.style.width = `0%`;
      }
    }
  }

  useEffect(() => {
    if (!videoElement?.current || videoPopup === undefined) return;

    if (videoPopup) {
      videoElement.current.pause();
      if (animationFrameId?.current) {
        window.cancelAnimationFrame(animationFrameId.current);
      }
    } else if (!videoPopup) {
      // Just playing will also trigger the animation.
      void videoElement.current.play();
    }
  }, [videoPopup]);

  return !Array.isArray(useableVideos) || useableVideos.length === 0 ? null : (
    <section>
      <Container containerClass="grid gap-6 md:gap-12 lg:gap-20 py-10">
        <VideoPopup
          isOpen={!!videoPopup}
          callback={() => setVideoPopup(null)}
          isPrismicVideo
          videoSourceUrl={videoPopup ?? ""}
        ></VideoPopup>
        {/* Image + titles of video */}
        <div className="relative aspect-14/15 items-center px-5 py-8 sm:aspect-1.75/1 md:grid md:grid-cols-8 md:gap-8 md:px-10 md:py-16 lg:aspect-2/1 lg:grid-cols-12 lg:px-0 lg:py-16">
          {/* Thumbnail */}
          <div className="absolute left-0 top-0 h-full w-full">
            <div className="absolute left-0 top-0 z-10 h-full w-full bg-black/60"></div>
            <video
              autoPlay
              muted
              playsInline
              src={
                useableVideos[currentIndex]?.videopreview?.node?.mediaItemUrl ??
                undefined
              }
              className="absolute left-0 top-0 h-full w-full bg-black object-cover"
              onLoadedMetadata={handleLoadMetaData}
              onPlay={handleVideoPlay}
              onEnded={handleVideoEnd}
              // loading="lazy"
              ref={videoElement}
            ></video>
          </div>
          <div className="col-span-full md:grid md:grid-cols-8 md:gap-8 lg:grid-cols-12">
            {/* Video title */}
            <div className="absolute z-10 w-11/12 place-self-start md:relative md:col-span-5 md:w-full lg:col-span-7 lg:col-start-2 xl:col-span-5 xl:col-start-2">
              {Array.isArray(useableVideos) &&
                useableVideos.map((video, i) => (
                  <h3
                    className={clsx(
                      "absolute left-0 top-0 w-full text-heading-4 text-white transition-opacity",
                      currentIndex === i
                        ? "opacity-100 delay-300"
                        : "opacity-0",
                    )}
                    key={i}
                  >
                    {video?.title}
                  </h3>
                ))}
            </div>
            {/* Other title */}
            <div
              className="absolute top-0 z-10 grid w-full md:relative md:col-span-3 lg:col-start-9"
              ref={scrollContainer}
            >
              <div
                className="relative grid h-full w-full gap-6 px-5 md:px-0 lg:gap-16"
                ref={progressItemsContainer}
              >
                {Array.isArray(useableVideos) &&
                  useableVideos.map((video, i) => {
                    return (
                      <button
                        key={i}
                        className={clsx(
                          "content-start gap-6 md:grid",
                          currentIndex === i ? "grid" : "hidden",
                        )}
                        onClick={() =>
                          setVideoPopup(video?.video?.node?.mediaItemUrl)
                        }
                      >
                        <h4 className="hidden text-heading-6 text-white/80 md:block">
                          {video?.title}
                        </h4>
                        <div className="relative h-0.5 w-full overflow-hidden">
                          <div className="absolute left-0 top-0 h-full w-full rounded bg-white/20" />
                          <span
                            className={clsx(
                              "absolute left-0 top-0 z-10 h-full rounded bg-white",
                              currentIndex > i
                                ? "w-full"
                                : "transition-all duration-75 ease-out",
                            )}
                          ></span>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
        {(title || linkTitle) && (
          <div className="grid items-start justify-items-start gap-x-8 gap-y-5 md:grid-cols-8 md:items-center md:px-10 lg:grid-cols-12 lg:px-0">
            {title && (
              <h3 className="text-heading-5 md:col-span-5 lg:col-span-7 lg:col-start-2 xl:col-span-6 xl:col-start-2">
                {title}
              </h3>
            )}
            {linkTitle && (
              <div className="md:col-span-3 lg:col-start-10">
                <CallToAction to={link?.url}>{linkTitle}</CallToAction>
              </div>
            )}
          </div>
        )}
      </Container>
    </section>
  );
}
